import "./bootstrap";
// ============================================================
// FILE: resources/js/app.js
// USPATIH GO — Main JavaScript Entry Point
//
// URUTAN IMPORT WAJIB:
//   1. Alpine (core)
//   2. Config (axios dulu, baru echo)
//   3. Helpers & Plugins (toast harus sebelum stores yg bisa memanggilnya)
//   4. Stores (auth dulu karena notifications bergantung padanya)
//   5. Components
//   6. Security (setelah semua siap, sebelum Alpine.start)
//   7. Alpine.start() — SELALU PALING TERAKHIR
// ============================================================

// ---- 1. Alpine Core -----------------------------------------
import Alpine from "alpinejs";
import focus from "@alpinejs/focus";
import persist from "@alpinejs/persist";
import collapse from "@alpinejs/collapse";
import "./inko-design/studio.js";

Alpine.plugin(focus);
Alpine.plugin(persist);
Alpine.plugin(collapse);
window.Alpine = Alpine;

// ---- 2. Config ----------------------------------------------
import "./utils/sessionCleanup.js"; // window.__uspClearClientSession
import "./config/axios.js"; // axios global headers & window.axios
import axios from "axios";
window.axios = axios;

import "./config/echo.js"; // window.Echo & window.Pusher

// ---- 3. Helpers & Plugins -----------------------------------
import "./helpers/formatters.js"; // window.formatRupiah, formatDate, timeAgo
import "./helpers/utils.js"; // window.debounce, copyToClipboard
import "./helpers/renderQr.js"; // window.renderQrCanvas, renderQrById
import "./helpers/passwordToggle.js"; // toggle show/hide password fields
import "./plugins/toast.js"; // window.toast  ← harus sebelum security
import "./plugins/confirm.js"; // window.confirmDialog / confirmAction
import "./plugins/theme.js"; // window.ThemeManager (auto-init di dalam file)
import "./plugins/navbar.js"; // scroll effect on #main-navbar
import "./plugins/lucide-icons.js";
import {
    reportClientError,
    toUserFacingMessage,
} from "./helpers/userFacingError.js";

// Global JS errors → laravel.log (tanpa toast teknis ke user)
if (typeof window !== "undefined") {
    window.addEventListener("error", (event) => {
        const msg = event?.message || event?.error?.message || "window_error";
        reportClientError({
            message: msg,
            source: "window_error",
            stack: event?.error?.stack || null,
            extra: {
                filename: event?.filename,
                lineno: event?.lineno,
                colno: event?.colno,
            },
        });
    });
    window.addEventListener("unhandledrejection", (event) => {
        const reason = event?.reason;
        const msg = reason?.message || String(reason || "unhandledrejection");
        reportClientError({
            message: msg,
            source: "unhandledrejection",
            stack: reason?.stack || null,
        });
    });
    window.toUserFacingMessage = toUserFacingMessage;
}

// ---- 4. Alpine Stores (bergantung pada helpers di atas) -----
import "./stores/auth.js"; // Alpine.store('auth')
import "./stores/cart.js"; // Alpine.store('cart')
import "./stores/wishlist.js"; // Alpine.store('wishlist')
import "./stores/notifications.js"; // Alpine.store('notifications')
import "./stores/theme.js"; // Alpine.store('theme')

// ---- 5. Alpine Components -----------------------------------
import "./components/errorScreen.js"; // Alpine.data('errorScreen') — halaman error
import "./components/otpInput.js"; // Alpine.data('otpInput')
import "./components/phoneInput.js"; // Alpine.data('phoneInput')
import "./components/countdown.js"; // Alpine.data('countdown')
import "./components/treasureHunt.js"; // Alpine.data('treasureHunt')
import "./components/ads.js"; // Alpine.data('adSlot', 'adRewarded')
import "./components/notificationSoundSettings.js"; // Alpine.data('notificationSoundSettings')
import "./components/priceCalc.js"; // Alpine.data('priceCalc')
import "./components/heroPrintSlider.js"; // Alpine.data('heroPrintSlider')
import "./pages/home/printCalculator.js"; // Alpine.data('kalkulatorFile')
import "./pages/flowers/lockedCanvas.js"; // window.LockedCanvas — artboard editor
import "./pages/flowers/templateBuilder.js"; // Alpine.data('flowerTemplateBuilder')
import "./pages/home/index.js"; // scroll reveal, nav spy (homepage)
import "./pages/shop/index.js"; // shop product, cart, checkout, payment
import "./pages/framio/studio.js"; // Alpine.data('framioStudio') — canvas editor
import "./pages/dashboard/index.js"; // customer dashboard components
import "./pages/dashboard/quizPlay.js"; // Alpine.data('quizPlay')
import "./pages/dashboard/bio.js"; // Alpine.data('dashboardBio')
import "./pages/auth/index.js"; // login/register OTP (Firebase)
import "./helpers/printThermal.js"; // window.printThermal
import {
    goToInvitationCreate,
    invitationCreatePath,
    invitationCheckoutPath,
    requireInvitationAuth,
    resolveInvitationCreateTarget,
} from "./helpers/invitationAuth.js";
window.goToInvitationCreate = goToInvitationCreate;
window.invitationCreatePath = invitationCreatePath;
window.invitationCheckoutPath = invitationCheckoutPath;
window.resolveInvitationCreateTarget = resolveInvitationCreateTarget;
window.requireInvitationAuth = requireInvitationAuth;
import "./pages/kasir/spkForm.js"; // Alpine.data('spkForm')
import "./pages/kasir/spkPreview.js"; // Alpine.data('spkPreview')
import "./pages/kasir/spkInvoice.js"; // Alpine.data('spkInvoice')
import "./pages/kasir/spkReprint.js"; // Alpine.data('spkReprint')
import "./pages/kasir/spkIndex.js"; // Alpine.data('spkIndex')
import "./pages/kasir/kasirLayout.js"; // Alpine.data('kasirLayout')
import "./pages/kasir/kasirNotifications.js"; // Alpine.store('kasirNotifications')
import "./pages/creator/creatorNotifications.js"; // Alpine.store('creatorNotifications')
import "./pages/kasir/kasirOrders.js"; // Alpine.data('kasirOrders')
import "./pages/kasir/kasirOrderDetail.js"; // Alpine.data('kasirOrderDetail')
import "./pages/kasir/kasirPriceSimulation.js"; // Alpine.data('kasirPriceSimulation')
import "./pages/kasir/kasirProducts.js"; // Alpine.data('kasirProducts')
import "./pages/kasir/kasirProductForm.js"; // Alpine.data('kasirProductForm')
import "./pages/kasir/kasirProductCategories.js"; // Alpine.data('kasirProductCategories')
import "./pages/kasir/kasirCategoryVariants.js"; // Alpine.data('kasirCategoryVariants')
import "./pages/kasir/kasirNews.js"; // Alpine.data('kasirNews')
import "./pages/kasir/kasirNewsForm.js"; // Alpine.data('kasirNewsForm')
import "./pages/kasir/kasirSocialMedia.js"; // Alpine.data('kasirSocialMedia')
import "./pages/kasir/kasirQuiz.js"; // Alpine.data('kasirQuiz')
import "./pages/kasir/kasirFlashSale.js"; // Alpine.data('kasirFlashSale')
import "./pages/kasir/kasirBattle.js"; // Alpine.data('kasirBattle')
import "./pages/kasir/kasirMembershipCards.js"; // Alpine.data('kasirMembershipCards')
import "./pages/kasir/kasirAffiliates.js"; // Alpine.data('kasirAffiliates')
import "./pages/kasir/kasirYoutubeQuests.js"; // Alpine.data('kasirYoutubeQuests')
import "./pages/kasir/kasirMascotQuests.js"; // Alpine.data('kasirMascotQuests')
import "./pages/kasir/kasirTreasureHuntSettings.js"; // Alpine.data('kasirTreasureHuntSettings')
import "./pages/kasir/kasirQrScan.js"; // Alpine.data('kasirQrScan')
import "./pages/kasir/kasirMyDesign.js"; // Alpine.data('kasirMyDesign')
import "./pages/kasir/kasirMaintenanceMode.js"; // Alpine.data('kasirMaintenanceMode')
import "./pages/kasir/kasirTvDisplay.js"; // Alpine.data('kasirTvDisplay')
import "./pages/kasir/kasirInkoJump.js"; // Alpine.data('kasirInkoJump')
import "./pages/kasir/kasirStokis.js"; // Alpine.data('kasirStokis')
import "./pages/kasir/kasirComics.js"; // Alpine.data('kasirComics')
import "./pages/kasir/kasirInkoTarangSettings.js"; // Alpine.data('kasirInkoTarangSettings')
import "./pages/kasir/kasirSeasonalThemes.js"; // Alpine.data('kasirSeasonalThemes')
import "./pages/kasir/kasirSeasonalThemeBuilder.js"; // Alpine.data('kasirSeasonalThemeBuilder')
import "./pages/kasir/kasirInvitationTemplates.js"; // Alpine.data('kasirInvitationTemplates')
import "./pages/kasir/kasirInvitationTemplateCreate.js"; // Alpine.data('kasirInvitationTemplateCreate')
import "./pages/kasir/kasirInvitationMarketplace.js"; // Alpine.data('kasirInvitationMarketplace')
import "./pages/kasir/kasirInvitationThemeSettings.js"; // Alpine.data('kasirInvitationThemeSettings')
import "./pages/kasir/kasirInvitationThemeReviews.js"; // Alpine.data('kasirInvitationThemeReviews')
import "./pages/kasir/kasirInvitationThemeVersions.js"; // Alpine.data('kasirInvitationThemeVersions')
import "./pages/kasir/kasirInvitationThemeAnalytics.js"; // Alpine.data('kasirInvitationThemeAnalytics')
import "./pages/kasir/kasirFlowerTemplates.js"; // Alpine.data('kasirFlowerTemplates')
import "./pages/kasir/kasirInvitationTemplateDesign.js"; // Alpine.data('kasirInvitationTemplateDesign')
import "./pages/shared/invitationCatalogManager.js"; // Alpine.data('invitationCatalogManager')
import "./pages/kasir/kasirAds.js"; // Alpine.data('kasirAds')
import "./pages/kasir/kasirAppContent.js"; // Alpine.data('kasirAppContent')
import "./pages/kasir/kasirCreatorAppContent.js"; // Alpine.data('kasirCreatorAppContent')
import "./pages/kasir/kasirVouchers.js"; // Alpine.data('kasirVouchers')
import "./pages/invitations/builder.js"; // Alpine.data('invBuilder')
import "./pages/invitations/templateCheckout.js"; // Alpine.data('invitationTemplateCheckout')
import "./pages/news/detail.js"; // Alpine.data('newsArticleDetail')
import "./pages/tools/removeBg.js"; // Alpine.data('removeBgTool')
import "./pages/tools/inkoTarang.js"; // Alpine.data('inkoTarangTool')

// ---- 6. Security Plugin (membaca window.SECURITY_CONFIG) ----
// window.SECURITY_CONFIG di-inject oleh layouts/app.blade.php
// toast sudah siap di langkah 3, sehingga pesan klik kanan bisa muncul
import "./plugins/security.js";

// ---- 7. Start Alpine (HARUS PALING TERAKHIR) ----------------
Alpine.start();
